import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Tabs, Tab, Card, CardContent, CardActions,
  Button, Chip, Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, Paper, CircularProgress, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Collapse, IconButton,
  Stepper, Step, StepLabel, StepContent,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { format } from 'date-fns';
import { dmsApi } from '../services/dmsApi';
import useDmsPalette from '../hooks/useDmsPalette';

function StatusChip({ status }) {
  const MP = useDmsPalette();
  const STATUS_MAP = {
    pending: { color: MP.yellow, label: 'Pending' },
    approved: { color: MP.green, label: 'Approved' },
    rejected: { color: '#ef4444', label: 'Rejected' },
    cancelled: { color: MP.textSecondary, label: 'Cancelled' },
  };
  const s = STATUS_MAP[status] || { color: MP.textSecondary, label: status };
  return <Chip label={s.label} size="small" sx={{ bgcolor: `${s.color}22`, color: s.color, fontWeight: 600, fontSize: '0.72rem' }} />;
}

export default function WorkflowManager() {
  const MP = useDmsPalette();
  const [tab, setTab] = useState(0);
  const [awaitingAction, setAwaitingAction] = useState([]);
  const [initiatedByMe, setInitiatedByMe] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Action dialog
  const [actionDialog, setActionDialog] = useState(null); // { id, action: 'approve'|'reject' }
  const [comment, setComment] = useState('');
  const [acting, setActing] = useState(false);

  // Expand rows
  const [expanded, setExpanded] = useState({});

  const loadPending = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await dmsApi.myPendingWorkflows();
      setAwaitingAction(data.awaitingAction || data.workflows || []);
      setInitiatedByMe(data.initiatedByMe || []);
    } catch (err) {
      setError(err.error || err.message || 'Failed to load pending workflows');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await dmsApi.listWorkflows();
      setWorkflows(data.workflows || data || []);
    } catch (err) {
      setError(err.error || err.message || 'Failed to load workflows');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 0 || tab === 1) loadPending();
    else loadAll();
  }, [tab, loadPending, loadAll]);

  const handleAction = async () => {
    if (!actionDialog) return;
    if (actionDialog.action === 'reject' && !comment.trim()) return;
    setActing(true);
    try {
      await dmsApi.actOnWorkflow(actionDialog.id, {
        action: actionDialog.action,
        comment: comment.trim() || undefined,
      });
      setActionDialog(null);
      setComment('');
      if (tab === 0 || tab === 1) loadPending(); else loadAll();
    } catch (err) {
      setError(err.error || err.message || 'Action failed');
    } finally {
      setActing(false);
    }
  };

  const toggleExpand = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom sx={{ fontSize: { xs: '1.2rem', md: '1.5rem' } }}>Workflows</Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Manage document approval workflows
      </Typography>

      <Tabs
        value={tab} onChange={(_, v) => setTab(v)}
        sx={{ mb: 3, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 } }}
      >
        <Tab label="Awaiting My Action" />
        <Tab label="Initiated by Me" />
        <Tab label="All Workflows" />
      </Tabs>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>
      ) : tab === 0 ? (
        /* ── Awaiting My Action ── */
        awaitingAction.length === 0 ? (
          <Box sx={{ textAlign: 'center', mt: 8, color: MP.textSecondary }}>
            <AssignmentIcon sx={{ fontSize: 56, opacity: 0.3, mb: 1 }} />
            <Typography variant="h6">No workflows awaiting your action</Typography>
            <Typography variant="body2">You're all caught up!</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {awaitingAction.map(wf => (
              <Card key={wf._id}>
                <CardContent sx={{ pb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <AssignmentIcon sx={{ color: MP.yellow, fontSize: 20 }} />
                    <Typography variant="subtitle1" fontWeight={600}>
                      {wf.documentTitle || wf.document?.title || 'Document'}
                    </Typography>
                    <StatusChip status={wf.status || 'pending'} />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Workflow: <strong>{wf.type || wf.workflowType || 'Approval'}</strong>
                    {' · '}Step: <strong>{wf.currentStepName || wf.currentStep?.name || '—'}</strong>
                  </Typography>
                  {wf.initiatedBy && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      Initiated by {wf.initiatedBy.name || wf.initiatedBy} · {wf.createdAt && format(new Date(wf.createdAt), 'MMM d, yyyy')}
                    </Typography>
                  )}
                </CardContent>
                <CardActions sx={{ px: 2, pb: 1.5 }}>
                  <Button
                    variant="contained" color="success" size="small"
                    startIcon={<CheckCircleIcon />}
                    onClick={() => setActionDialog({ id: wf._id, action: 'approve' })}
                  >
                    Approve
                  </Button>
                  <Button
                    variant="outlined" color="error" size="small"
                    startIcon={<CancelIcon />}
                    onClick={() => setActionDialog({ id: wf._id, action: 'reject' })}
                  >
                    Reject
                  </Button>
                </CardActions>
              </Card>
            ))}
          </Box>
        )
      ) : tab === 1 ? (
        initiatedByMe.length === 0 ? (
          <Box sx={{ textAlign: 'center', mt: 8, color: MP.textSecondary }}>
            <AssignmentIcon sx={{ fontSize: 56, opacity: 0.3, mb: 1 }} />
            <Typography variant="h6">No active workflows initiated by you</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {initiatedByMe.map((wf) => (
              <Card key={wf._id}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {wf.documentId?.title || 'Document'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {wf.type} · Step {wf.currentStep + 1} of {wf.steps?.length || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Current: {wf.steps?.[wf.currentStep]?.name || '—'}
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button size="small" color="warning" onClick={() => dmsApi.cancelWorkflow(wf._id).then(loadPending)}>
                    Cancel Workflow
                  </Button>
                </CardActions>
              </Card>
            ))}
          </Box>
        )
      ) : (
        /* ── All Workflows Tab ── */
        <TableContainer component={Paper} sx={{ bgcolor: MP.surface, border: `1px solid ${MP.border}` }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.75rem', color: MP.textSecondary, borderColor: MP.border } }}>
                <TableCell>Document</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Current Step</TableCell>
                <TableCell>Initiated By</TableCell>
                <TableCell>Date</TableCell>
                <TableCell width={48} />
              </TableRow>
            </TableHead>
            <TableBody>
              {workflows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: 'center', py: 6, color: MP.textSecondary }}>
                    No workflows found
                  </TableCell>
                </TableRow>
              ) : (
                workflows.map(wf => (
                  <React.Fragment key={wf._id}>
                    <TableRow hover sx={{ '&:hover': { bgcolor: `${MP.blue}08` } }}>
                      <TableCell sx={{ fontSize: '0.82rem', fontWeight: 500 }}>
                        {wf.documentTitle || wf.document?.title || '—'}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', textTransform: 'capitalize' }}>
                        {wf.type || wf.workflowType || '—'}
                      </TableCell>
                      <TableCell><StatusChip status={wf.status} /></TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>
                        {wf.currentStepName || wf.currentStep?.name || '—'}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>
                        {wf.initiatedBy?.name || wf.initiatedBy || '—'}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                        {wf.createdAt ? format(new Date(wf.createdAt), 'MMM d, yyyy') : '—'}
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => toggleExpand(wf._id)}>
                          {expanded[wf._id] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={7} sx={{ py: 0, borderBottom: expanded[wf._id] ? undefined : 'none' }}>
                        <Collapse in={expanded[wf._id]} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 2, px: 1 }}>
                            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                              Workflow Steps
                            </Typography>
                            {(wf.steps || []).length === 0 ? (
                              <Typography variant="caption" color="text.secondary">No step history available</Typography>
                            ) : (
                              <Stepper orientation="vertical" nonLinear sx={{ mt: 1 }}>
                                {wf.steps.map((step, idx) => {
                                  const isActive = idx === wf.currentStep && wf.status === 'active';
                                  const isDone = step.status === 'approved';
                                  const isRejected = step.status === 'rejected';
                                  const canAct = awaitingAction.some((p) => p._id === wf._id);
                                  return (
                                    <Step key={idx} completed={isDone} active={isActive}>
                                      <StepLabel
                                        error={isRejected}
                                        icon={
                                          isDone ? <CheckCircleIcon color="success" sx={{ fontSize: 20 }} /> :
                                          isRejected ? <CancelIcon color="error" sx={{ fontSize: 20 }} /> :
                                          isActive ? <HourglassEmptyIcon color="warning" sx={{ fontSize: 20 }} /> :
                                          undefined
                                        }
                                      >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                          <Typography variant="body2" fontWeight={isActive ? 700 : 400}>
                                            {step.name || `Step ${idx + 1}`}
                                          </Typography>
                                          <Chip size="small" label={step.status || 'pending'}
                                            color={isDone ? 'success' : isRejected ? 'error' : isActive ? 'warning' : 'default'}
                                            sx={{ fontSize: '0.65rem' }}
                                          />
                                          {step.actedAt && (
                                            <Typography variant="caption" color="text.secondary">
                                              {new Date(step.actedAt).toLocaleDateString()}
                                            </Typography>
                                          )}
                                        </Box>
                                      </StepLabel>
                                      {(step.comment || (isActive && canAct)) && (
                                        <StepContent>
                                          {step.comment && (
                                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                              "{step.comment}"
                                            </Typography>
                                          )}
                                          {isActive && canAct && (
                                            <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                                              <Button size="small" variant="contained" color="success"
                                                onClick={() => setActionDialog({ id: wf._id, action: 'approve' })}
                                              >Approve</Button>
                                              <Button size="small" variant="outlined" color="error"
                                                onClick={() => setActionDialog({ id: wf._id, action: 'reject' })}
                                              >Reject</Button>
                                            </Box>
                                          )}
                                        </StepContent>
                                      )}
                                    </Step>
                                  );
                                })}
                              </Stepper>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── Action Dialog ── */}
      <Dialog open={!!actionDialog} onClose={() => { setActionDialog(null); setComment(''); }} maxWidth="sm" fullWidth>
        <DialogTitle>
          {actionDialog?.action === 'approve' ? '✅ Approve Workflow' : '❌ Reject Workflow'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth multiline rows={3}
            label={actionDialog?.action === 'reject' ? 'Reason (required)' : 'Comment (optional)'}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={actionDialog?.action === 'reject' ? 'Please provide a reason for rejection…' : 'Add an optional comment…'}
            required={actionDialog?.action === 'reject'}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setActionDialog(null); setComment(''); }}>Cancel</Button>
          <Button
            variant="contained"
            color={actionDialog?.action === 'approve' ? 'success' : 'error'}
            onClick={handleAction}
            disabled={acting || (actionDialog?.action === 'reject' && !comment.trim())}
          >
            {acting ? <CircularProgress size={20} /> : actionDialog?.action === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
